// Report-schedule chips — GET /api/v1/report-schedules.
// Relative words and titles use the clinic clock (America/New_York), never
// the browser's. Same trap that made every task OVERDUE on 2026-08-17.

import { CLINIC_TIME_ZONE, clinicToday } from './clinicDay';

export const REPORT_SCHEDULE_STATES = [
  'arrived_today',
  'expected_waiting',
  'overdue',
  'paused',
] as const;

export type ReportScheduleState = (typeof REPORT_SCHEDULE_STATES)[number];

export interface PresenceDay {
  date: string;
  received: boolean;
  runAt: string | null;
}

export interface ReportSchedule {
  id: number;
  locationCode: string;
  label: string;
  reportCode: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | string;
  expectedAt: string | null;
  active: boolean;
  driveFolderId: string | null;
  folderUrl: string | null;
  state: ReportScheduleState;
  neverArrived: boolean;
  lastReceivedAt: string | null;
  lastRunAt: string | null;
  lastFileUrl: string | null;
  received7: number;
  expected7: number;
  received30: number;
  expected30: number;
  presence: PresenceDay[];
}

export interface ReportScheduleList {
  schedules: ReportSchedule[];
}

export function reportSchedulesKey(clinic: string): string {
  return `report-schedules:${clinic}`;
}

const STATE_LABEL: Record<ReportScheduleState, string> = {
  arrived_today: 'arrived today',
  expected_waiting: 'expected — not yet due',
  overdue: 'expected — overdue',
  paused: 'paused',
};

export function scheduleStateLabel(state: ReportScheduleState): string {
  return STATE_LABEL[state] ?? state;
}

function clinicDateOf(input: Date): string {
  return clinicToday(input);
}

function clinicDayDiff(fromYmd: string, toYmd: string): number {
  const [fy, fm, fd] = fromYmd.split('-').map(Number);
  const [ty, tm, td] = toYmd.split('-').map(Number);
  const from = new Date(fy, fm - 1, fd).getTime();
  const to = new Date(ty, tm - 1, td).getTime();
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

/** Last received, in relative words, against the clinic calendar. */
export function formatLastReceived(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never';
  const ms = now.getTime() - then.getTime();
  if (ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = clinicDayDiff(clinicDateOf(then), clinicDateOf(now));
  if (days <= 1) return 'yesterday';
  return `${days} days ago`;
}

export function formatClinicDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function presenceTitle(day: PresenceDay): string {
  if (day.runAt) return `${day.date} · ${formatClinicDateTime(day.runAt)}`;
  return `${day.date} · not received`;
}

export function groupSchedulesByCode(schedules: ReportSchedule[]): {
  reportCode: string;
  schedules: ReportSchedule[];
}[] {
  const groups: { reportCode: string; schedules: ReportSchedule[] }[] = [];
  const index = new Map<string, number>();
  for (const row of schedules) {
    const code = row.reportCode || '';
    const at = index.get(code);
    if (at === undefined) {
      index.set(code, groups.length);
      groups.push({ reportCode: code, schedules: [row] });
    } else {
      groups[at].schedules.push(row);
    }
  }
  return groups;
}
